import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Box, Chip, Divider, Drawer, List, ListItemButton, ListItemText, ListSubheader, Toolbar, Tooltip,
  Typography, Button,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { BrandMark } from './BrandMark';
import { networkLabel } from './common';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { APP_VERSION, COMPANY } from '../version';

const DRAWER = 248;

/**
 * 메뉴 분류(`group`)는 **접근 권한(`roles`)과 다른 축**이다.
 *
 * 예: 「청약 공고 관리」는 `issuer_st`·`admin` 이 함께 접근하지만(컨트랙트 배포 단계가 관리자 전용),
 * 업무로 보면 **발행자 화면**이다. 접근 목록으로 분류하면 이 화면이 관리자 구역에도 나타나
 * 되레 구분이 흐려진다. 그래서 "누가 들어갈 수 있는가"(roles)와 "무엇을 하는 화면인가"(group)를
 * 분리해 적는다.
 */
type NavGroup = 'user' | 'issuer_st' | 'issuer_sc' | 'admin' | 'common';

interface NavItem {
  to: string;
  label: string;
  roles?: string[]; // 접근 권한. 없으면 로그인 사용자 공통
  group: NavGroup; // 메뉴에서 묶이는 구역
}

const NAV: NavItem[] = [
  { to: '/subscriptions', label: '청약 공고', roles: ['user'], group: 'user' },
  { to: '/assets', label: '내 자산', roles: ['user'], group: 'user' },
  { to: '/tx', label: '거래 · 요청 내역', roles: ['user'], group: 'user' },

  // 컨트랙트 배포(②)가 관리자 전용이라 관리자도 접근하지만, 업무 구역은 발행자다
  { to: '/issuer/subscriptions', label: '청약 공고 관리', roles: ['issuer_st', 'admin'], group: 'issuer_st' },
  { to: '/issuer/securities', label: '자산 관리·통제', roles: ['issuer_st'], group: 'issuer_st' },

  { to: '/issuer/stablecoin', label: '스테이블코인 관리', roles: ['issuer_sc'], group: 'issuer_sc' },

  { to: '/admin/signups', label: '가입 승인', roles: ['admin'], group: 'admin' },
  { to: '/admin/roles', label: '권한 관리', roles: ['admin'], group: 'admin' },
  { to: '/admin/keys', label: '키 관리', roles: ['admin'], group: 'admin' },
  { to: '/admin/registry', label: '레지스트리 계정', roles: ['admin'], group: 'admin' },
  { to: '/admin/monitoring', label: '정합성 · 모니터링', roles: ['admin'], group: 'admin' },

  // 여러 역할이 함께 쓰는 화면 — 특정 역할 구역에 넣으면 어느 쪽에 두어도 어색하다
  { to: '/explorer', label: '블록체인 조회', roles: ['admin', 'issuer_st', 'issuer_sc'], group: 'common' },
  { to: '/mypage', label: '마이페이지', group: 'common' }, // 로그인 사용자 공통(계정 정보·비밀번호 변경)
];

/** 구역 표시 순서 — 사용자 업무 → 발행 업무 → 운영 → 공통 */
const GROUP_ORDER: NavGroup[] = ['user', 'issuer_st', 'issuer_sc', 'admin', 'common'];

/** 구역 식별색 — 머리글의 점에만 쓴다(항목까지 물들이면 활성 표시와 싸운다) */
const GROUP_COLOR: Record<NavGroup, string> = {
  user: '#1a56d6',
  issuer_st: '#0f766e',
  issuer_sc: '#b45309',
  admin: '#9333ea',
  common: '#64748b',
};

const GROUP_LABEL: Record<NavGroup, string> = {
  user: '서비스 사용자',
  issuer_st: '발행자 · 토큰증권',
  issuer_sc: '발행자 · 스테이블코인',
  admin: '시스템 관리자',
  common: '공통',
};

const ROLE_LABEL: Record<string, string> = {
  user: '서비스 사용자',
  issuer_st: '발행자 · 토큰증권',
  issuer_sc: '발행자 · 스테이블코인',
  admin: '시스템 관리자',
};

export function Layout({ children }: { children: ReactNode }) {
  const { me, logout, hasRole } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const items = NAV.filter((i) => !i.roles || hasRole(...i.roles));

  // 접근 가능한 항목을 구역으로 묶는다. 빈 구역은 버린다.
  const sections = GROUP_ORDER
    .map((g) => ({ group: g, items: items.filter((i) => i.group === g) }))
    .filter((s) => s.items.length > 0);

  // 머리글이 필요한 것은 **권한이 겹친 사용자**뿐이다. 단일 역할이면 예전처럼 평평하게 보여준다.
  // '공통'은 누구에게나 있으므로 겹침 판정에서 뺀다 — 넣으면 일반 사용자에게도 머리글이 생긴다.
  const grouped = sections.filter((x) => x.group !== 'common').length > 1;
  // 머리글의 네트워크 표시 — 연결된 체인 ID 로 판정한다(하드코딩하면 환경마다 틀린다)
  const health = useQuery({
    queryKey: ['health-chain'],
    queryFn: () => api.get<{ chain?: { chainId?: string } }>('/health'),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: 1201, bgcolor: '#0b1f3a' }} elevation={0}>
        <Toolbar sx={{ gap: 2 }}>
          <BrandMark size={30} />
          <Typography sx={{ fontWeight: 800 }}>디지털자산 검증 인프라</Typography>
          <Chip
            size="small"
            label={`● ${networkLabel(health.data?.chain?.chainId)}`}
            sx={{ bgcolor: 'rgba(255,255,255,.1)', color: '#8fb6f0', fontWeight: 700 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          {me && (
            <>
              <Box sx={{ textAlign: 'right', mr: 1 }}>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>{me.name || me.loginId}</Typography>
                <Typography variant="caption" sx={{ color: '#8fb6f0' }}>
                  {me.roles.map((r) => ROLE_LABEL[r] ?? r).join(', ') || '역할 없음'}
                </Typography>
              </Box>
              <Button size="small" onClick={() => { logout(); nav('/login'); }} sx={{ color: '#fff' }}>
                로그아웃
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{ width: DRAWER, [`& .MuiDrawer-paper`]: { width: DRAWER, boxSizing: 'border-box', bgcolor: '#fbfcfe', display: 'flex', flexDirection: 'column' } }}
      >
        <Toolbar />
        <List sx={{ px: 1.5, pt: grouped ? 0.5 : 2, flex: 1, overflowY: 'auto' }} subheader={<li />}>
          {sections.map((sec) => (
            <li key={sec.group}>
              <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                {grouped && (
                  <ListSubheader
                    disableSticky
                    sx={{
                      bgcolor: 'transparent', px: 1.5, pt: 1.75, pb: 0.25, lineHeight: 1.5,
                      fontSize: 11, fontWeight: 800, letterSpacing: '.04em', color: 'text.secondary',
                      display: 'flex', alignItems: 'center', gap: 0.75,
                    }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: GROUP_COLOR[sec.group], flexShrink: 0 }} />
                    {GROUP_LABEL[sec.group]}
                    {/* 내가 갖지 않은 역할의 화면인데 접근 권한으로 보이는 경우를 알려준다 */}
                    {sec.group !== 'common' && !hasRole(sec.group) && (
                      <Tooltip title="이 역할은 없지만 다른 권한으로 접근할 수 있는 화면입니다">
                        <Chip
                          size="small"
                          label="권한 접근"
                          sx={{ ml: 0.75, height: 16, fontSize: 10, fontWeight: 700 }}
                        />
                      </Tooltip>
                    )}
                  </ListSubheader>
                )}
                {sec.items.map((i) => {
                  const active = loc.pathname === i.to || loc.pathname.startsWith(i.to + '/');
                  return (
                    <ListItemButton
                      key={i.to}
                      component={Link}
                      to={i.to}
                      selected={active}
                      sx={{
                        borderRadius: 2,
                        mb: 0.25,
                        py: grouped ? 0.6 : 1,
                        '&.Mui-selected': { bgcolor: '#e8f0ff', color: '#1a56d6', fontWeight: 700 },
                      }}
                    >
                      <ListItemText primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 500 }} primary={i.label} />
                    </ListItemButton>
                  );
                })}
              </ul>
            </li>
          ))}
          {items.length === 0 && (
            <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
              승인 후 이용 가능합니다.
            </Typography>
          )}
        </List>

        {/* 좌측 네비 하단 — 회사 · 버전 */}
        <Box sx={{ mt: 'auto', px: 2, py: 2, flexShrink: 0 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary' }}>
            {COMPANY}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace' }}>
            디지털자산 검증 인프라 {APP_VERSION}
          </Typography>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 4 }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
